import { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { MessageCircle, UserPlus, Plus, X, Search, EyeOff, MessageSquarePlus, Clock, CheckCircle2, ArrowRight, ExternalLink, Phone as PhoneIcon, Trash2, Pencil } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../../lib/api";
import { useTaxonomy } from "../../lib/useTaxonomy";
import { PlayerForm, emptyPlayer } from "./Players";

const inputCls = "w-full bg-[#0F0F0F] border border-white/10 text-white text-sm px-3 py-2.5 rounded-sm placeholder:text-white/30 focus:outline-none focus:border-[#D4AF37]";
const labelCls = "block text-[10px] uppercase tracking-widest text-white/40 mb-1.5";

const LEAD_STATUSES = ["Opened Conversation", "Invited for Trial", "Attended Trial", "Converted", "Dead"];
const OPEN_STATUSES = ["Opened Conversation", "Invited for Trial", "Attended Trial"];
const CLOSED_STATUSES = ["Converted", "Dead"];
const REFERRAL_SOURCES = [
  "Instagram", "Facebook", "Google Search", "WhatsApp",
  "Referral (Friend/Family)", "Existing Parent", "School", "Walk-in",
  "Event / Tournament", "Flyer / Poster", "Other",
];

const STATUS_STYLE = {
  "Opened Conversation": "border-blue-400/40 text-blue-300",
  "Invited for Trial": "border-yellow-400/40 text-yellow-200",
  "Attended Trial": "border-[#D4AF37]/60 text-[#D4AF37]",
  "Converted": "border-green-400/50 text-green-300",
  "Dead": "border-red-400/50 text-red-300",
};


const waLink = (phone) => {
  const digits = phone.replace(/\D/g, "");
  return `https://wa.me/${digits.length === 10 ? "91" : ""}${digits}`;
};

const todayIST = () => new Date().toLocaleDateString("en-CA", { timeZone: "Asia/Kolkata" });
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const ordinal = (n) => `${n}${[11, 12, 13].includes(n % 100) ? "th" : ["th", "st", "nd", "rd"][n % 10] || "th"}`;
// "17th Nov 2026" — accepts YYYY-MM-DD or a full ISO timestamp (shown in IST).
export const niceDate = (iso) => {
  if (!iso) return "";
  const d = iso.length <= 10 ? iso : new Date(iso).toLocaleDateString("en-CA", { timeZone: "Asia/Kolkata" });
  return `${ordinal(Number(d.slice(8, 10)))} ${MONTHS[Number(d.slice(5, 7)) - 1]} ${d.slice(0, 4)}`;
};
const niceTime = (iso) => new Date(iso).toLocaleTimeString("en-IN", { hour: "numeric", minute: "2-digit", timeZone: "Asia/Kolkata" });

const LeadNotes = ({ t }) => {
  const notes = [
    ...(t.notes ? [{ id: "initial", text: t.notes, created_at: t.created_at, initial: true }] : []),
    ...(t.updates || []),
  ].reverse();
  if (!notes.length) return <span className="text-white/30 text-xs">No notes yet.</span>;
  return (
    <ul className="space-y-1.5" data-testid={`lead-notes-${t.id}`}>
      {notes.map((n) => (
        <li key={n.id} className="flex gap-2 text-xs">
          <MessageSquarePlus size={13} className="text-[#D4AF37] mt-0.5 shrink-0" />
          <p className="min-w-0">
            <span className="text-white/85 whitespace-pre-wrap break-words">{n.text}</span>
            <span className="text-white/35 ml-2 whitespace-nowrap">· {n.initial ? "Initial note · " : ""}{n.created_at ? niceDate(n.created_at) : ""}</span>
          </p>
        </li>
      ))}
    </ul>
  );
};

const leadMonth = (t) => (t.date || t.created_at || "").slice(0, 7);
const monthName = (m) => new Date(`${m}-01T00:00:00Z`).toLocaleDateString("en-IN", { month: "long", year: "numeric", timeZone: "UTC" });

const LeadMonthSummary = ({ leads, monthFilter, setMonthFilter, months }) => {
  const count = (s) => leads.filter((t) => (t.lead_status || "Opened Conversation") === s).length;
  const converted = count("Converted");
  const dead = count("Dead");
  const rate = leads.length ? Math.round((converted / leads.length) * 100) : 0;
  const cells = [["Leads", leads.length, "text-white"], ["Converted", converted, "text-green-300"], ["Dead", dead, "text-red-300"], ["Still Open", leads.length - converted - dead, "text-[#D4AF37]"], ["Conversion", `${rate}%`, "text-[#D4AF37]"]];
  return (
    <div className="mb-6" data-testid="lead-month-summary">
      <div className="flex flex-wrap items-center gap-3 mb-3">
        <span className="text-[10px] uppercase tracking-[0.24em] text-white/40">Month</span>
        <select value={monthFilter} onChange={(e) => setMonthFilter(e.target.value)} className="bg-[#0F0F0F] border border-white/15 text-white text-sm px-3 py-2 rounded-sm focus:outline-none focus:border-[#D4AF37]" data-testid="lead-month-filter">
          <option value="">All months</option>
          {months.map((m) => <option key={m} value={m}>{monthName(m)}</option>)}
        </select>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-px bg-white/10 border border-white/10">
        {cells.map(([label, v, cls]) => (
          <div key={label} className="bg-[#0A0A0A] p-4" data-testid={`lead-summary-${label.toLowerCase().replace(/\s+/g, "-")}`}>
            <p className="text-[10px] uppercase tracking-widest text-white/40 mb-1">{label}</p>
            <p className={`font-heading text-2xl font-black ${cls}`}>{v}</p>
          </div>
        ))}
      </div>
    </div>
  );
};

/* ------------ Add Lead modal (now with referral source) ------------ */

const AddLeadModal = ({ lead, onClose, onSaved }) => {
  const { batchNames } = useTaxonomy();
  const knownSource = !lead?.source || REFERRAL_SOURCES.includes(lead.source);
  const [form, setForm] = useState(lead ? {
    name: lead.name || "", phone: lead.phone || "", batch: lead.batch || "", date: lead.date || "",
    dob: lead.dob || "", year_of_birth: lead.year_of_birth || "", location: lead.location || "", notes: lead.notes || "",
    source: knownSource ? (lead.source || "") : "Other", source_other: knownSource ? "" : lead.source,
  } : {
    name: "", phone: "", batch: "", date: todayIST(),
    dob: "", year_of_birth: "", location: "", notes: "",
    source: "", source_other: "",
  });
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    const finalSource = form.source === "Other" ? form.source_other.trim() : form.source;
    const { source_other, ...rest } = form;
    try {
      const body = { ...rest, dob: form.dob || null, source: finalSource || (lead ? "" : null) };
      if (lead) await api.put(`/admin/trials/${lead.id}`, body);
      else await api.post("/admin/trials", body);
      toast.success(lead ? "Lead updated" : "Lead added");
      onSaved(); onClose();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  return (
    <div className="fixed inset-0 z-[60] bg-black/80 backdrop-blur-sm flex items-start justify-center px-4 overflow-y-auto py-10" onClick={onClose} data-testid="lead-modal">
      <form onSubmit={submit} onClick={(e) => e.stopPropagation()} className="bg-[#0A0A0A] border border-white/10 p-6 sm:p-8 w-full max-w-lg">
        <div className="flex justify-between items-start mb-5">
          <h2 className="font-heading text-xl font-black">{lead ? "Edit Lead" : "Add Lead"}</h2>
          <button type="button" onClick={onClose} className="text-white/50 hover:text-white"><X size={20} /></button>
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          <div className="sm:col-span-2"><label className={labelCls}>Player Name *</label><input required value={form.name} onChange={set("name")} className={inputCls} data-testid="lead-name" /></div>
          <div><label className={labelCls}>Guardian Phone *</label><input required value={form.phone} onChange={set("phone")} className={inputCls} placeholder="98736 28804" data-testid="lead-phone" /></div>
          <div><label className={labelCls}>Batch (interested in)</label>
            <select value={form.batch} onChange={set("batch")} className={inputCls} data-testid="lead-batch">
              <option value="">—</option>
              {batchNames.map((b) => <option key={b} value={b}>{b}</option>)}
            </select></div>
          <div><label className={labelCls}>Year of Birth *</label><input required placeholder="e.g. 2014" value={form.year_of_birth} onChange={set("year_of_birth")} className={inputCls} data-testid="lead-yob" /></div>
          <div className="sm:col-span-2"><label className={labelCls}>Player Location</label><input placeholder="Neighbourhood / Area" value={form.location} onChange={set("location")} className={inputCls} data-testid="lead-location" /></div>
          <div className="sm:col-span-2"><label className={labelCls}>How did you hear about us?</label>
            <select value={form.source} onChange={set("source")} className={inputCls} data-testid="lead-source">
              <option value="">— Select source —</option>
              {REFERRAL_SOURCES.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          {form.source === "Other" && (
            <div className="sm:col-span-2"><label className={labelCls}>Specify source</label>
              <input value={form.source_other} onChange={set("source_other")} className={inputCls} data-testid="lead-source-other" placeholder="e.g. Podcast, Instagram Story from…" />
            </div>
          )}
          <div className="sm:col-span-2"><label className={labelCls}>Notes</label><textarea rows={3} value={form.notes} onChange={set("notes")} className={inputCls} data-testid="lead-notes" /></div>
          <div><label className={labelCls}>Trial Date</label><input required type="date" value={form.date} onChange={set("date")} className={inputCls} data-testid="lead-date" /></div>
        </div>
        <div className="flex gap-3 pt-6">
          <button type="submit" className="bg-[#D4AF37] text-black font-bold uppercase tracking-widest text-xs px-8 py-3 rounded-sm hover:bg-[#F3D059] transition-colors duration-200" data-testid="lead-save">{lead ? "Save Changes" : "Save Lead"}</button>
          <button type="button" onClick={onClose} className="border border-white/20 text-white/70 text-xs uppercase tracking-widest px-6 py-3 rounded-sm hover:border-[#D4AF37]">Cancel</button>
        </div>
      </form>
    </div>
  );
};

/* ------------ Convert Lead → Player modal (uses PlayerForm from Players.jsx) ------------ */

const ConvertToPlayerModal = ({ trial, onClose, onLinked }) => {
  const initial = {
    ...emptyPlayer,
    name: trial.name || "",
    guardian_phone: trial.phone || "",
    batch: trial.batch || "",
    joining_date: new Date().toISOString().slice(0, 10),
    source: "Manual",
    notes: `Converted from lead ${trial.trial_no || ""}${trial.source ? ` · Referral: ${trial.source}` : ""}`.trim(),
  };

  const save = async (form, plan) => {
    try {
      const { data: created } = await api.post("/admin/players", {
        ...form,
        dob: form.dob || null,
        joining_date: form.joining_date || null,
        leaving_date: form.leaving_date || null,
      });
      if (plan) {
        try {
          await api.post(`/admin/players/${created.player_id}/fee-plans`, plan);
        } catch (err) {
          toast.error(`Player added but plan failed: ${formatApiError(err)}`);
        }
      }
      await api.post(`/admin/trials/${trial.id}/link-player`, { player_id: created.player_id });
      toast.success(`${trial.name} added to academy as ${created.player_id}`);
      onLinked(created.player_id);
      onClose();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  return (
    <div className="fixed inset-0 z-[55] bg-black/85 backdrop-blur-sm flex items-start justify-center px-4 overflow-y-auto py-10" onClick={onClose} data-testid="convert-modal">
      <div onClick={(e) => e.stopPropagation()} className="bg-[#0A0A0A] border border-[#D4AF37]/40 p-6 sm:p-8 w-full max-w-3xl">
        <div className="flex justify-between items-start mb-6">
          <div>
            <p className="text-[10px] tracking-[0.28em] uppercase text-[#D4AF37] font-semibold">Convert Lead → Player</p>
            <h2 className="font-heading text-xl font-black mt-1">{trial.name} <span className="text-[#D4AF37] text-sm ml-1">{trial.trial_no}</span></h2>
            <p className="text-white/50 text-xs mt-1">Pre-filled from the lead. Add any missing details and (optionally) the initial fee plan.</p>
          </div>
          <button onClick={onClose} className="text-white/50 hover:text-white"><X size={20} /></button>
        </div>
        <PlayerForm initial={initial} onSave={save} onClose={onClose} saveLabel="Add To Academy" showPlan />
      </div>
    </div>
  );
};

/* ------------ Lead Detail (source, timeline, convert affordance) ------------ */

const LeadDetailModal = ({ trial, onClose, onSaved }) => {
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  const [convertOpen, setConvertOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const status = trial.lead_status || "Opened Conversation";
  const updates = [...(trial.updates || [])].reverse();

  const addUpdate = async (e) => {
    e.preventDefault();
    if (!note.trim()) return;
    setSaving(true);
    try {
      await api.post(`/admin/trials/${trial.id}/updates`, { text: note });
      toast.success("Update added");
      setNote("");
      onSaved();
    } catch (err) { toast.error(formatApiError(err)); }
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-start justify-center px-4 overflow-y-auto py-10" onClick={onClose} data-testid="trial-detail-modal">
      <div onClick={(e) => e.stopPropagation()} className="bg-[#0A0A0A] border border-white/10 p-6 sm:p-8 w-full max-w-xl">
        <div className="flex justify-between items-start mb-5">
          <div>
            <p className="text-xs tracking-[0.25em] uppercase text-[#D4AF37] font-semibold">{trial.trial_no}</p>
            <h2 className="font-heading text-xl font-black mt-1">{trial.name}</h2>
            <span className={`inline-block mt-2 text-[10px] uppercase tracking-widest px-2 py-1 border ${STATUS_STYLE[status] || "border-white/20 text-white/40"}`}>{status}</span>
          </div>
          <button onClick={onClose} className="text-white/50 hover:text-white"><X size={20} /></button>
        </div>

        <div className="grid grid-cols-2 gap-3 text-sm mb-6">
          <p><span className="text-white/40 text-[10px] uppercase tracking-widest block mb-0.5">Phone</span>{trial.phone}</p>
          <p><span className="text-white/40 text-[10px] uppercase tracking-widest block mb-0.5">Batch</span>{trial.batch || "—"}</p>
          <p><span className="text-white/40 text-[10px] uppercase tracking-widest block mb-0.5">Trial Date</span>{niceDate(trial.date)}</p>
          {trial.year_of_birth && <p><span className="text-white/40 text-[10px] uppercase tracking-widest block mb-0.5">Year of Birth</span>{trial.year_of_birth}</p>}
          {trial.location && <p><span className="text-white/40 text-[10px] uppercase tracking-widest block mb-0.5">Location</span>{trial.location}</p>}
          <p className="col-span-2"><span className="text-white/40 text-[10px] uppercase tracking-widest block mb-0.5">Heard about us via</span>
            {trial.source ? <span className="text-[#D4AF37]" data-testid="detail-source">{trial.source}</span> : <span className="text-white/30">Not recorded</span>}
          </p>
          {trial.notes && <p className="col-span-2"><span className="text-white/40 text-[10px] uppercase tracking-widest block mb-0.5">Initial Notes · {niceDate(trial.created_at)}</span><span className="whitespace-pre-wrap">{trial.notes}</span></p>}
        </div>

        {/* Convert to player / linked player banner */}
        {status === "Converted" && (
          trial.player_id ? (
            <div className="border border-green-400/40 bg-green-400/5 p-4 mb-6 flex items-center justify-between gap-3" data-testid="lead-added-banner">
              <div>
                <p className="text-[10px] tracking-[0.24em] uppercase text-green-300 font-semibold">Added To Academy</p>
                <p className="text-white/80 text-sm mt-1">Linked player: <span className="text-[#D4AF37] font-semibold">{trial.player_id}</span></p>
              </div>
              <CheckCircle2 size={22} className="text-green-300 shrink-0" />
            </div>
          ) : (
            <button
              onClick={() => setConvertOpen(true)}
              className="w-full mb-6 inline-flex items-center justify-center gap-2 bg-[#D4AF37] hover:bg-[#F3D059] text-black font-bold uppercase tracking-widest text-xs px-6 py-3 rounded-sm transition-colors"
              data-testid="lead-add-to-academy"
            >
              Add To Academy <ArrowRight size={14} />
            </button>
          )
        )}

        {/* Update timeline */}
        <h3 className="text-[10px] tracking-[0.24em] uppercase text-white/40 font-semibold mb-3 flex items-center gap-2">
          <Clock size={12} className="text-[#D4AF37]" /> Update Log
        </h3>
        <form onSubmit={addUpdate} className="flex gap-2 mb-4">
          <input
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Add a new update (e.g. Called, will visit Saturday)…"
            className={inputCls}
            data-testid="lead-update-input"
          />
          <button
            type="submit"
            disabled={saving || !note.trim()}
            className="bg-[#D4AF37] text-black font-bold uppercase tracking-widest text-[10px] px-4 rounded-sm hover:bg-[#F3D059] transition-colors disabled:opacity-40"
            data-testid="lead-update-save"
          >
            {saving ? "…" : "Save"}
          </button>
        </form>

        {updates.length === 0 ? (
          <p className="text-white/40 text-xs border border-dashed border-white/10 p-4 text-center" data-testid="lead-updates-empty">
            No updates recorded yet. Every note you save here shows up chronologically with a timestamp.
          </p>
        ) : (
          <ul className="space-y-2 max-h-80 overflow-y-auto pr-1" data-testid="lead-updates-list">
            {updates.map((u) => (
              <li key={u.id} className="border-l-2 border-[#D4AF37]/50 pl-3 py-1">
                <p className="text-white/85 text-sm whitespace-pre-wrap">{u.text}</p>
                <p className="text-white/40 text-[10px] uppercase tracking-widest mt-1">
                  {niceDate(u.created_at)} · {niceTime(u.created_at)}
                  {u.created_by && <span className="ml-2">· {u.created_by}</span>}
                </p>
              </li>
            ))}
          </ul>
        )}
        <p className="text-white/30 text-[10px] mt-4">Added {niceDate(trial.created_at)} · {niceTime(trial.created_at)}</p>

        {/* Edit + Delete actions */}
        <div className="flex items-center gap-2 pt-4 mt-4 border-t border-white/5" data-testid="lead-actions">
          <button
            onClick={() => setEditOpen(true)}
            className="flex items-center gap-2 text-xs uppercase tracking-widest text-white/70 border border-white/15 px-3 py-2 hover:border-[#D4AF37] hover:text-[#D4AF37]"
            data-testid="lead-edit-btn"
          >
            <Pencil size={13} /> Edit
          </button>
          <button
            onClick={async () => {
              if (!window.confirm(`Delete lead "${trial.name}"? This cannot be undone.`)) return;
              try { await api.delete(`/admin/trials/${trial.id}`); toast.success("Lead deleted"); onClose(); onSaved(); }
              catch (err) { toast.error(formatApiError(err)); }
            }}
            className="flex items-center gap-2 text-xs uppercase tracking-widest text-red-400/90 border border-red-400/40 px-3 py-2 hover:bg-red-500/10 ml-auto"
            data-testid="lead-delete-btn"
          >
            <Trash2 size={13} /> Delete
          </button>
        </div>

        {convertOpen && createPortal(<ConvertToPlayerModal trial={trial} onClose={() => setConvertOpen(false)} onLinked={() => { setConvertOpen(false); onSaved(); }} />, document.body)}
        {editOpen && createPortal(<AddLeadModal lead={trial} onClose={() => setEditOpen(false)} onSaved={onSaved} />, document.body)}
      </div>
    </div>
  );
};

/* ------------ Row + quick inline update ------------ */

const Row = ({ t, onOpen, onStatus, onQuickUpdate, view }) => {
  const [openInput, setOpenInput] = useState(false);
  const [text, setText] = useState("");
  const [saving, setSaving] = useState(false);
  const status = t.lead_status || "Opened Conversation";
  const stagesForView = view === "closed" ? CLOSED_STATUSES : OPEN_STATUSES;

  const save = async () => {
    if (!text.trim()) return;
    setSaving(true);
    const ok = await onQuickUpdate(t, text);
    setSaving(false);
    if (ok) { setText(""); setOpenInput(false); }
  };

  return (
    <div className="border border-white/10 bg-[#0A0A0A] p-5 space-y-3" data-testid={`trial-row-${t.id}`}>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <button onClick={() => onOpen(t)} className="text-left min-w-0 flex-1">
          <p className="font-semibold text-sm">{t.name} <span className="text-[#D4AF37] text-xs ml-1">{t.trial_no}</span></p>
          <p className="text-white/40 text-xs mt-0.5 flex flex-wrap gap-x-3 gap-y-0.5">
            {t.year_of_birth && <span>YoB <span className="text-white/70">{t.year_of_birth}</span></span>}
            {t.location && <span>· {t.location}</span>}
            {t.source && <span>· <span className="text-[#D4AF37]/80">{t.source}</span></span>}
            {view === "closed" && t.player_id && <span className="text-green-300">· {t.player_id}</span>}
          </p>
        </button>
        <div className="flex items-center gap-2 flex-wrap">
          <span className={`text-[10px] uppercase tracking-widest px-2 py-1 border ${STATUS_STYLE[status] || "border-white/20 text-white/40"}`} data-testid={`lead-status-badge-${t.id}`}>{status}</span>
          <select
            value={status}
            onChange={(e) => onStatus(t, e.target.value)}
            className="bg-[#0F0F0F] border border-white/15 text-white/80 text-xs px-3 py-2 rounded-sm focus:outline-none focus:border-[#D4AF37]"
            data-testid={`lead-status-${t.id}`}
          >
            {LEAD_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
          <a href={`tel:${t.phone}`}
            className="flex items-center gap-2 text-white/80 text-xs uppercase tracking-widest border border-white/20 px-3 py-2 hover:border-[#D4AF37] hover:text-[#D4AF37] transition-colors duration-200"
            data-testid={`trial-call-${t.id}`} title="Call">
            <PhoneIcon size={13} /> Call
          </a>
          <a href={waLink(t.phone)} target="_blank" rel="noreferrer"
            className="flex items-center gap-2 text-[#D4AF37] text-xs uppercase tracking-widest border border-[#D4AF37]/50 px-3 py-2 hover:bg-[#D4AF37] hover:text-black transition-colors duration-200"
            data-testid={`trial-wa-${t.id}`}>
            <MessageCircle size={13} /> WhatsApp
          </a>
        </div>
      </div>

      <div className="flex flex-wrap items-start gap-3 border-t border-white/5 pt-3">
        <div className="text-xs text-white/50 flex-1 min-w-0">
          <LeadNotes t={t} />
        </div>
        {!openInput ? (
          <button onClick={() => setOpenInput(true)} className="text-[10px] uppercase tracking-widest text-[#D4AF37]/80 hover:text-[#D4AF37] flex items-center gap-1" data-testid={`lead-inline-add-${t.id}`}>
            <MessageSquarePlus size={12} /> Add Update
          </button>
        ) : (
          <div className="flex gap-2 w-full">
            <input
              autoFocus
              value={text}
              onChange={(e) => setText(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") save(); if (e.key === "Escape") { setOpenInput(false); setText(""); } }}
              placeholder="What happened?"
              className={inputCls}
              data-testid={`lead-inline-input-${t.id}`}
            />
            <button onClick={save} disabled={saving || !text.trim()} className="bg-[#D4AF37] text-black font-bold uppercase tracking-widest text-[10px] px-4 rounded-sm hover:bg-[#F3D059] transition-colors disabled:opacity-40" data-testid={`lead-inline-save-${t.id}`}>
              {saving ? "…" : "Save"}
            </button>
            <button onClick={() => { setOpenInput(false); setText(""); }} className="text-white/50 text-xs uppercase tracking-widest px-2">Cancel</button>
          </div>
        )}
      </div>
    </div>
  );
};

/* ------------ Page ------------ */

export default function Trials() {
  const [trials, setTrials] = useState([]);
  const [view, setView] = useState("open"); // open | closed
  const [stageFilter, setStageFilter] = useState(new Set());
  const [sourceFilter, setSourceFilter] = useState("");
  const [search, setSearch] = useState("");
  const [monthFilter, setMonthFilter] = useState("");
  const [showAdd, setShowAdd] = useState(false);
  const [detail, setDetail] = useState(null);

  const load = () => api.get("/admin/trials").then((r) => setTrials(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  // If detail is open, keep it in sync when list refreshes
  useEffect(() => { if (detail) { const t = trials.find((x) => x.id === detail.id); if (t) setDetail(t); } }, [trials]); // eslint-disable-line react-hooks/exhaustive-deps

  const setStatus = async (t, status) => {
    try {
      await api.put(`/admin/trials/${t.id}/status`, { status });
      toast.success(`Marked as ${status}`);
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const quickUpdate = async (t, text) => {
    try {
      await api.post(`/admin/trials/${t.id}/updates`, { text });
      toast.success("Update added");
      load();
      return true;
    } catch (err) { toast.error(formatApiError(err)); return false; }
  };

  const toggleStage = (s) => {
    setStageFilter((prev) => { const n = new Set(prev); if (n.has(s)) n.delete(s); else n.add(s); return n; });
  };

  const monthTrials = useMemo(() => trials.filter((t) => !monthFilter || leadMonth(t) === monthFilter), [trials, monthFilter]);
  const allMonths = useMemo(() => [...new Set(trials.map(leadMonth).filter(Boolean))].sort().reverse(), [trials]);
  const openCount = monthTrials.filter((t) => OPEN_STATUSES.includes(t.lead_status || "Opened Conversation")).length;
  const closedCount = monthTrials.length - openCount;
  const currentStages = view === "open" ? OPEN_STATUSES : CLOSED_STATUSES;

  // Reset stage filter when switching views (its stage set no longer applies)
  useEffect(() => { setStageFilter(new Set()); }, [view]);

  const showingAllStages = stageFilter.size === 0;

  const filtered = monthTrials.filter((t) => {
    const s = t.lead_status || "Opened Conversation";
    if (view === "open" && !OPEN_STATUSES.includes(s)) return false;
    if (view === "closed" && !CLOSED_STATUSES.includes(s)) return false;
    if (!showingAllStages && !stageFilter.has(s)) return false;
    if (sourceFilter && (t.source || "") !== sourceFilter) return false;
    if (search && !`${t.name} ${t.phone} ${t.trial_no || ""}`.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  const counts = useMemo(() => {
    const c = {}; currentStages.forEach((s) => { c[s] = 0; });
    monthTrials.forEach((t) => {
      const s = t.lead_status || "Opened Conversation";
      if (currentStages.includes(s)) c[s] = (c[s] || 0) + 1;
    });
    return c;
  }, [monthTrials, currentStages]);

  const sourceStats = useMemo(() => {
    const m = new Map();
    trials.forEach((t) => { const s = t.source || "Not recorded"; m.set(s, (m.get(s) || 0) + 1); });
    return [...m.entries()].sort((a, b) => b[1] - a[1]);
  }, [trials]);
  const allSources = useMemo(() => [...new Set(trials.map((t) => t.source).filter(Boolean))].sort(), [trials]);

  return (
    <div data-testid="admin-trials">
      <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
        <h1 className="font-heading text-3xl font-black uppercase tracking-tight">
          Leads <span className="text-[#D4AF37]">({filtered.length})</span>
        </h1>
        <button onClick={() => setShowAdd(true)} className="flex items-center gap-2 bg-[#D4AF37] text-black font-bold uppercase tracking-widest text-xs px-5 py-2.5 rounded-sm hover:bg-[#F3D059] transition-colors duration-200" data-testid="trial-add">
          <Plus size={15} /> Add Lead
        </button>
      </div>

      {/* Open / Closed sub-tabs */}
      <LeadMonthSummary leads={monthTrials} monthFilter={monthFilter} setMonthFilter={setMonthFilter} months={allMonths} />
      <div className="flex items-center gap-2 mb-6 border-b border-white/10" data-testid="lead-view-tabs">
        {[
          ["open", `Open Leads`, openCount],
          ["closed", `Closed Leads`, closedCount],
        ].map(([v, label, cnt]) => (
          <button
            key={v}
            onClick={() => setView(v)}
            className={`px-4 py-3 text-xs uppercase tracking-widest font-semibold border-b-2 transition-colors ${
              view === v ? "border-[#D4AF37] text-[#D4AF37]" : "border-transparent text-white/50 hover:text-white"
            }`}
            data-testid={`lead-view-${v}`}
          >
            {label} <span className={`ml-1 ${view === v ? "text-[#D4AF37]" : "text-white/40"}`}>({cnt})</span>
          </button>
        ))}
      </div>

      {/* Source stats */}
      {sourceStats.length > 0 && (
        <div className="mb-6 border border-white/10 p-4" data-testid="lead-source-stats">
          <p className="text-[10px] tracking-[0.24em] uppercase text-white/40 mb-3">Where leads are coming from · lifetime</p>
          <div className="flex flex-wrap gap-2">
            {sourceStats.slice(0, 10).map(([src, n]) => (
              <span key={src} className="text-xs border border-white/15 px-3 py-1.5 flex items-center gap-2">
                <span className="text-white/70">{src}</span>
                <span className="text-[#D4AF37] font-bold">{n}</span>
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="space-y-4 mb-6">
        <div className="flex flex-wrap items-center gap-3">
          <div className="relative flex-1 min-w-[220px] max-w-md">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
            <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search name, phone or lead #" className={inputCls + " pl-9"} data-testid="lead-search" />
          </div>
          {allSources.length > 0 && (
            <select value={sourceFilter} onChange={(e) => setSourceFilter(e.target.value)} className={inputCls + " max-w-xs"} data-testid="lead-source-filter">
              <option value="">All sources</option>
              {allSources.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          )}
        </div>

        <div className="flex flex-wrap items-center gap-2" data-testid="lead-status-filter">
          <span className="text-[10px] uppercase tracking-[0.24em] text-white/40 mr-1">Stages</span>
          <button onClick={() => setStageFilter(new Set())}
            className={`text-xs uppercase tracking-widest px-4 py-2 border transition-colors ${showingAllStages ? "bg-[#D4AF37] text-black border-[#D4AF37]" : "border-white/15 text-white/60 hover:border-[#D4AF37]"}`}
            data-testid="lead-filter-all">
            All {view === "open" ? `(${openCount})` : `(${closedCount})`}
          </button>
          {currentStages.map((s) => {
            const active = stageFilter.has(s);
            return (
              <button key={s} onClick={() => toggleStage(s)} aria-pressed={active}
                className={`text-xs uppercase tracking-widest px-4 py-2 border transition-colors ${active ? "bg-[#D4AF37] text-black border-[#D4AF37]" : "border-white/15 text-white/60 hover:border-[#D4AF37]"}`}
                data-testid={`lead-filter-${s.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                {s} <span className={`ml-1.5 ${active ? "text-black/60" : "text-white/40"}`}>{counts[s] || 0}</span>
              </button>
            );
          })}
          {(!showingAllStages || sourceFilter || search) && (
            <button onClick={() => { setStageFilter(new Set()); setSourceFilter(""); setSearch(""); }} className="text-[10px] uppercase tracking-widest text-white/40 hover:text-[#D4AF37] px-3" data-testid="lead-filter-clear">Clear</button>
          )}
        </div>
      </div>

      <div className="space-y-2">
        {filtered.map((t) => (
          <Row key={t.id} t={t} onOpen={setDetail} onStatus={setStatus} onQuickUpdate={quickUpdate} view={view} />
        ))}
        {filtered.length === 0 && (
          <p className="text-white/40 text-sm border border-white/10 p-6 text-center" data-testid="lead-empty">
            <UserPlus className="inline-block mr-2" size={14} />
            {view === "open" ? "No open leads matching this view." : "No closed leads yet."}
          </p>
        )}
      </div>

      {showAdd && <AddLeadModal onClose={() => setShowAdd(false)} onSaved={load} />}
      {detail && <LeadDetailModal trial={detail} onClose={() => setDetail(null)} onSaved={load} />}
    </div>
  );
}
